// Central content for the New Beginning counselling website.
// Contact details are placeholders — update phone/email/location as needed.

export const SITE = {
  name: "New Beginning",
  tagline: "Counselling & Emotional Wellbeing",
  counsellor: "Harish B S",
  website: "www.newbeginning.care",
  phone: "+91 99672 67920",
  email: "harish@newbeginning.care",
  location: "Mumbai, India",
};

export const HERO = {
  overline: "Counselling & Therapy",
  title: "Every New Beginning Starts with One Conversation.",
  body: [
    "Life can feel overwhelming at times. Whether you're struggling with anxiety, stress, relationship challenges, grief, career uncertainty, emotional burnout, or simply feeling stuck, you don't have to navigate it alone.",
    "At New Beginning, we offer a safe, confidential, and non-judgmental space where you can pause, reflect, heal, and move forward at your own pace.",
    "We believe that every person has the capacity for growth, resilience, and positive change. Sometimes, all it takes is the right support to discover it.",
  ],
  cta: "Book Your First Session",
};

export const WELCOME = {
  overline: "Welcome",
  title: "Welcome to New Beginning",
  body: [
    "Life doesn't always go according to plan. Relationships end. Careers become uncertain. Stress builds quietly. Loss changes us. Sometimes we simply lose sight of ourselves.",
    "Seeking support isn't a sign of weakness — it is an act of courage.",
    "Our aim is to help individuals better understand themselves, develop healthier ways of coping, strengthen relationships, and build emotional well-being that lasts beyond the counselling room.",
    "Every session is centred around respect, empathy, confidentiality, and genuine human connection.",
  ],
};

export const WHY_CHOOSE = [
  "Confidential",
  "Compassionate",
  "Ethical Practice",
  "Individualised Support",
  "Evidence-Informed Approaches",
  "Safe & Non-Judgmental Environment",
  "Online & In-Person Sessions",
  "Focus on Long-Term Emotional Well-being",
];

export const ABOUT = {
  intro: {
    overline: "About Me",
    title: "Meet Harish B S",
    body: [
      "Hello, and welcome. I'm Harish B S, a counsellor with a deep interest in emotional well-being, personal growth, and helping individuals navigate life's challenges with clarity and confidence.",
      "My journey into counselling began with a simple belief — that every individual deserves a safe space where they can speak openly without fear of judgment.",
      "Over the years, I have invested in continuous professional learning across counselling, trauma-informed practice, neuro-linguistic programming (NLP), hypnotherapy, relationship recovery, and emotional healing. These experiences have strengthened my ability to understand diverse human experiences while keeping the individual — not the technique — at the centre of every session.",
      "My approach is warm, collaborative, and client-centred. Rather than telling you what to do, I work alongside you to understand your experiences, identify your strengths, and support you in creating meaningful and lasting change.",
      "I believe counselling is not about fixing people. It is about helping people reconnect with the strengths they already possess.",
    ],
  },
  philosophy: {
    overline: "My Philosophy",
    title: "Healing is never rushed. Growth is never forced.",
    lines: [
      "Every person's story is unique.",
      "There is no single method that works for everyone.",
      "Some people need someone to truly listen.",
      "Some need practical strategies.",
      "Some need emotional healing.",
      "Some need clarity during difficult life decisions.",
    ],
    close:
      "At New Beginning, counselling is tailored to your individual needs while respecting your pace, values, and personal experiences. Every step forward matters.",
  },
  workWith: {
    overline: "Who I Work With",
    title: "Support for every stage of life",
    intro:
      "I work with adolescents, young adults, working professionals, couples, and individuals experiencing emotional or personal challenges.",
    items: [
      "Anxiety and excessive worrying",
      "Stress and burnout",
      "Low confidence and self-esteem",
      "Relationship difficulties",
      "Breakup and divorce recovery",
      "Grief and loss",
      "Career confusion",
      "Emotional overwhelm",
      "Life transitions",
      "Work-related stress",
      "Student concerns",
      "Personal growth and self-discovery",
    ],
    close:
      "You don't need to wait until life becomes unbearable before seeking support. Sometimes counselling is simply about understanding yourself better.",
  },
  approach: {
    overline: "My Approach",
    title: "Empathy paired with practical guidance",
    body: [
      "I believe effective counselling combines empathy with practical guidance. My work is primarily client-centred while integrating evidence-informed approaches based on individual needs.",
      "Where appropriate and with informed consent, sessions may incorporate elements drawn from:",
    ],
    methods: [
      "Counselling Psychology",
      "Neuro-Linguistic Programming (NLP)",
      "Hypnotherapy",
      "Trauma-informed practices",
      "Solution-focused techniques",
      "Mindfulness-based approaches",
    ],
    close: "The focus is always on what best supports your goals.",
  },
  qualifications: {
    overline: "Qualifications",
    title: "Professional Development",
    items: [
      "PG Diploma in Counselling (TISS)",
      "Diploma in Neuro-Linguistic Programming (NLP)",
      "Certified Hypnotherapist – Level 1",
      "Fellowship in Trauma, Addiction & Healing Pathways",
      "Certified in Breakup & Divorce Counselling",
    ],
  },
};

export const SERVICES = [
  {
    icon: "MessageCircleHeart",
    title: "Individual Counselling",
    desc: "A confidential one-to-one space where you can explore your thoughts, emotions, behaviours, and life experiences while working towards greater emotional well-being.",
  },
  {
    icon: "Wind",
    title: "Anxiety & Stress Management",
    desc: "Understand the causes of stress and anxiety while learning practical strategies to manage overwhelming thoughts, emotional responses, and everyday challenges.",
  },
  {
    icon: "Users",
    title: "Relationship Counselling",
    desc: "Support for navigating communication difficulties, conflicts, trust issues, emotional distance, and building healthier relationships.",
  },
  {
    icon: "HeartHandshake",
    title: "Breakup & Divorce Recovery",
    desc: "Healing after the end of a relationship can feel overwhelming. Together we work through grief, loss, emotional pain, rebuilding confidence, acceptance, and creating a healthy path forward.",
  },
  {
    icon: "Compass",
    title: "Career Counselling",
    desc: "Career decisions can often create confusion and anxiety. Counselling provides clarity by exploring your strengths, interests, values, decision-making patterns, and future goals.",
  },
  {
    icon: "GraduationCap",
    title: "Student Counselling",
    desc: "Helping students manage academic pressure, exam stress, career uncertainty, confidence issues, emotional well-being, and adjustment difficulties.",
  },
  {
    icon: "Sprout",
    title: "Personal Growth",
    desc: "Counselling is not only for people experiencing difficulties. Many seek counselling to improve self-awareness, emotional intelligence, confidence, communication, and overall quality of life.",
  },
];

export const WHAT_TO_EXPECT = {
  overline: "What to Expect",
  title: "Your first session is simply a conversation",
  intro: "There is no pressure. No expectations. No judgment.",
  steps: [
    "Understand your concerns",
    "Discuss your goals",
    "Explore your current challenges",
    "Develop an initial plan together",
  ],
  close: "Every session remains confidential, respectful, and focused on your needs.",
};

export const FAQS = [
  {
    q: "Is counselling confidential?",
    a: "Yes. Everything discussed during counselling remains confidential within the ethical and legal limits of professional practice.",
  },
  {
    q: "How many sessions will I need?",
    a: "The number of sessions depends on your goals, concerns, and personal circumstances. Some individuals benefit from short-term counselling, while others prefer ongoing support.",
  },
  {
    q: "Will you give advice?",
    a: "Rather than telling you what decisions to make, counselling helps you understand yourself better so that you can make informed and meaningful choices.",
  },
  {
    q: "Do I need to have a serious mental health issue?",
    a: "Not at all. Many people seek counselling for personal growth, life transitions, relationship concerns, career decisions, stress management, or simply to gain greater self-awareness.",
  },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1679060170179-c384186c87ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjYWxtJTIwc2VyZW5lJTIwbmF0dXJlJTIwc2FnZSUyMGdyZWVufGVufDB8fHx8MTc4MzM0NDYzM3ww&ixlib=rb-4.1.0&q=85",
  portrait: "https://customer-assets.emergentagent.com/job_design-deploy-60/artifacts/yafuc4q6_Profile%20Pic%20Harish.png",
  zen: "https://images.pexels.com/photos/26150937/pexels-photo-26150937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  room: "https://images.pexels.com/photos/6628520/pexels-photo-6628520.jpeg",
};
